import Immutable from 'immutable';
import {getCurrentDesign, getIconsArray} from '~/selectors/CanvasSelectors';
import {
  getViewsList,
  getAnchorStyleId,
  getSelections
} from '~/selectors/ProductSelectors';
import {isSameColor, mapSpotColorParts} from '~/utils/colors';
import {isNone} from '~/data/accessors/color';
import {
  getBackgroundSpotColor,
  isArt,
  isText,
  isUpload
} from '~/data/accessors/icon';
import {COLOR_MODES} from '~/constants/UploadConstants';
import {AUTOSAVE_EMAIL} from '~/constants/FundraisingConstants';
import {mapClipartColorToRFE} from '~/utils/clipart';
import {getPostalCode, getQuoteState} from '~/selectors/QuoteSelectors';
import {getNamesAndNumbers as getNamesAndNumbersSelector} from '~/selectors/NamesAndNumbersSelectors';

// This is a whitelist of icon properties that will be sent to RFE.
const ICON_PROPERTIES = [
  'align',
  'clipartId',
  'contrast',
  'crop',
  'effect',
  'fgcolor',
  'fit',
  'font',
  'fontCategory',
  'fontHeight',
  'height',
  'horizontalFlip',
  'invertContrast',
  'naturalHeight',
  'naturalWidth',
  'outline',
  'path',
  'rotation',
  'singleColor',
  'sizeIsDirty',
  'type',
  'url',
  'value',
  'verticalFlip',
  'viewId',
  'width',
  'x',
  'y'
];

export function serialize(
  state,
  designName,
  email,
  verifyEmail,
  sendEmail,
  deviceType,
  addAnchorOnly = false
) {
  const saveData = JSON.stringify(
    getSaveData(
      state,
      designName,
      email,
      verifyEmail,
      sendEmail,
      deviceType,
      addAnchorOnly
    )
  );
  return JSON.stringify({
    save_source: 'ndx',
    saveData
  });
}

export function getSaveData(
  state,
  designName,
  email,
  verifyEmail,
  sendEmail,
  deviceType,
  addAnchorOnly = false
) {
  const design = getCurrentDesign(state);
  return {
    designName,
    email,
    deviceType,
    id: design.get('designId'),
    designIdea: design.get('designIdeaId'),
    verifyEmail: verifyEmail || undefined,
    sendEmail: getSendEmailPreference(sendEmail),
    notes: '',
    quoteData: getQuoteData(state),
    personalization: getNamesAndNumbers(state),
    products: getProducts(state, addAnchorOnly),
    canvas: getCanvas(state),
    inkMatches: design.get('inkMatches')?.toJS() || {}
  };
}

export function serializeForFundraising(state) {
  const saveData = JSON.stringify(
    getSaveDataForFundraising(state, AUTOSAVE_EMAIL)
  );
  return JSON.stringify({
    save_source: 'ndx',
    saveData,
    autogenerateDesignName: true
  });
}

export function getSaveDataForFundraising(state, email) {
  return {
    email,
    products: getProducts(state),
    canvas: getCanvas(state)
  };
}

export function getSendEmailPreference(sendEmail = true) {
  return sendEmail;
}

export function getProducts(state, addAnchorOnly = false) {
  let selections = getSelections(state);

  // we need to put the anchor product at the head of the list
  const anchorStyle = getAnchorStyleId(state);
  const anchorProductIndex = selections.findIndex(
    s => s.get('styleId') === anchorStyle
  );
  if (anchorProductIndex !== -1) {
    // remove it and add it to the front
    const anchorProduct = selections.get(anchorProductIndex);

    if (addAnchorOnly) {
      selections = [anchorProduct];
    } else {
      selections = selections
        .remove(anchorProductIndex)
        .unshift(anchorProduct)
        .toArray();
    }
  } else {
    selections = selections.toArray();
  }

  return selections.map(selection => selection.get('colorId'));
}

export function getCanvas(state) {
  const views = getViewsList(state);
  const icons = getIconsArray(state);
  const serializedIcons = icons.map(icon => serializeIcon(icon));
  return views.map(view => {
    return {
      view: view.id,
      icons: serializedIcons.filter(icon => icon.viewId === view.id)
    };
  });
}

export function getQuoteData(state) {
  return {
    postalCode: getPostalCode(state),
    totalPrice:
      getQuoteState(state).get('hasFullQuote') &&
      getQuoteState(state).get('totalPrice'),
    quantities: getQuoteState(state).get('quantities').toJS()
  };
}

export function getNamesAndNumbers(state) {
  return getNamesAndNumbersSelector(state)
    .filter(n => n.name || n.number)
    .map(nameAndNumber => {
      const {name, number, productSize} = nameAndNumber.toObject();
      return {
        name,
        number,
        size: productSize
      };
    });
}

export function serializeIcon(icon) {
  const rawData = icon.toJS();
  let saveData = ICON_PROPERTIES.reduce((accumulator, property) => {
    if (Object.prototype.hasOwnProperty.call(rawData, property)) {
      accumulator[property] = rawData[property];
    }
    return accumulator;
  }, {});
  if (isArt(icon.type)) {
    saveData = {
      ...saveData,
      ...serializeArtIcon(icon)
    };
  } else if (isUpload(icon.type)) {
    saveData = {
      ...saveData,
      ...serializeUploadIcon(icon)
    };
  }
  if (isText(icon.type)) {
    saveData = {
      ...saveData,
      ...serializeTextIcon(icon)
    };
  }
  if (icon.color) {
    return {
      ...saveData,
      fgcolor: getColor(icon.color)
    };
  } else {
    return saveData;
  }
}

export function getColor(color) {
  if (!color) {
    return null;
  }
  const {r, g, b} = color;
  return [r, g, b];
}

export function serializeArtIcon(icon) {
  return {
    options: {
      lockRatio: icon.lockRatio,
      transparent: icon.transparent,
      blackwhite: icon.blackwhite,
      grayscale: icon.singleColor
    },
    colors: icon.colors
      .map(spotColor => {
        return mapSpotColorParts(spotColor, color => {
          color = mapClipartColorToRFE(color);
          return serializeColor(color);
        });
      })
      .toJS()
  };
}

function serializeTextIcon(icon) {
  return {
    effect: {
      type: icon.effectType,
      setting: icon.effectSetting
    },
    outline: {
      color: getColor(icon.outlineColor),
      width: icon.outlineWidth,
      clearFill: isNone(icon.color)
    },
    naturalHeight: icon.naturalHeight || icon.height,
    naturalWidth: icon.naturalWidth || icon.width
  };
}

function serializeUploadIcon(icon) {
  return {
    ...serializeColors(icon),
    ...serializeImageType(icon)
  };
}

function serializeColor(color) {
  return {
    red: color.red || 0,
    green: color.green || 0,
    blue: color.blue || 0,
    transparent: isNone(color)
  };
}

function serializeColors(icon) {
  const colors = (icon.spotColors || Immutable.List())
    .map(spotColor => {
      const backgroundSpotColor = getBackgroundSpotColor(icon);
      const isBackground =
        backgroundSpotColor &&
        isSameColor(backgroundSpotColor.original, spotColor.original);

      return Immutable.Map(spotColor)
        .map(serializeColor)
        .set('background', isBackground);
    })
    .toJS();
  return {colors};
}

function serializeImageType(icon) {
  const colorModeMap = {
    [COLOR_MODES.PHOTO]: 'fullColor',
    [COLOR_MODES.MONOCHROME]: 'monochrome',
    [COLOR_MODES.SPOT]: 'spotColor'
  };
  return {imageType: colorModeMap[icon.colorMode] || null};
}
